import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend, ComposedChart,
} from "recharts";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { registerTurkishFont } from "@/lib/pdfFont";

const COLORS = ["#0A0A0A", "#525252", "#059669", "#E11D48", "#F59E0B", "#A3A3A3", "#7C3AED", "#2563EB", "#78716C", "#404040"];

export default function Reports() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 5, 1).toISOString().slice(0, 10);
  const [start, setStart] = useState(first);
  const [end, setEnd] = useState(todayISO());
  const [by, setBy] = useState("date"); // date | due_date
  const [data, setData] = useState(null);

  // Contact monthly chart state
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState("");
  const [contactData, setContactData] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/reports/data?start=${start}&end=${end}&by=${by}`);
    setData(data);
  }, [start, end, by]);

  useEffect(() => {
    load();
    // Only run on mount; user re-triggers via "Uygula" button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const apply = () => load();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/contacts");
        const sorted = [...(data || [])].sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", "tr"),
        );
        setContacts(sorted);
      } catch (e) { /* noop */ }
    })();
  }, []);

  useEffect(() => {
    if (!contactId) { setContactData(null); return; }
    setContactLoading(true);
    api.get(`/reports/contact-monthly/${contactId}`)
      .then(({ data }) => setContactData(data))
      .catch(() => toast.error("Cari grafik verisi alınamadı"))
      .finally(() => setContactLoading(false));
  }, [contactId]);

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const rows = data.transactions.map((t) => ({
      "İşlem Tarihi": t.date,
      "Vade Tarihi": t.due_date || "",
      Tür: t.type === "income" ? "Gelir" : t.type === "expense" ? "Gider" : "Transfer",
      Kategori: t.category || "", Açıklama: t.description || "",
      Tutar: t.amount,
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "İşlemler");
    const sum = [
      ["PLASPER FİNANS - Finansal Rapor"],
      ["Plasper Ambalaj"],
      [""],
      ["Baz", by === "due_date" ? "Vade Tarihi" : "İşlem Tarihi"],
      ["Dönem", `${start} — ${end}`],
      [""],
      ["Toplam Gelir", data.total_income],
      ["Toplam Gider", data.total_expense],
      ["Net", data.net],
    ];
    const wsSum = XLSX.utils.aoa_to_sheet(sum);
    wsSum["!cols"] = [{ wch: 28 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, wsSum, "Özet");
    XLSX.writeFile(wb, `plasper_finans_rapor_${by}_${start}_${end}.xlsx`);
    toast.success("Excel indirildi");
  };

  const loadLogoDataUrl = async () => {
    try {
      const res = await fetch("/plasper-logo.jpg");
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onloadend = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
    } catch { return null; }
  };

  const exportPDF = async () => {
    if (!data) return;
    const doc = new jsPDF({ compress: true });
    await registerTurkishFont(doc);
    const logo = await loadLogoDataUrl();
    if (logo) {
      try { doc.addImage(logo, "JPEG", 14, 10, 40, 13, undefined, "FAST"); } catch {}
    }
    doc.setFontSize(18); doc.text("Plasper Finans - Finansal Rapor", 60, 20);
    doc.setFontSize(9); doc.text("Plasper Ambalaj", 60, 26);
    doc.setFontSize(10); doc.text(`Dönem: ${fmtDate(start)} — ${fmtDate(end)}`, 14, 38);
    doc.text(`Rapor bazı: ${by === "due_date" ? "Vade Tarihi" : "İşlem Tarihi"}`, 14, 44);
    doc.setFontSize(11);
    doc.text(`Toplam Gelir: ${fmtTRY(data.total_income)}`, 14, 54);
    doc.text(`Toplam Gider: ${fmtTRY(data.total_expense)}`, 14, 60);
    doc.text(`Net: ${fmtTRY(data.net)}`, 14, 66);
    autoTable(doc, {
      startY: 74, head: [["İşlem T.", "Vade T.", "Tür", "Kategori", "Açıklama", "Tutar"]],
      body: data.transactions.map((t) => [
        t.date,
        t.due_date || "-",
        t.type === "income" ? "Gelir" : t.type === "expense" ? "Gider" : "Transfer",
        t.category || "-", t.description || "-",
        fmtTRY(t.amount),
      ]),
      styles: { fontSize: 8, font: "Roboto" },
      headStyles: { fillColor: [10, 10, 10], font: "Roboto", fontStyle: "bold" },
      didDrawPage: (dat) => {
        doc.setFontSize(8); doc.setTextColor(120);
        doc.text(
          `Plasper Finans · Sayfa ${doc.internal.getNumberOfPages()}`,
          14, doc.internal.pageSize.getHeight() - 8,
        );
      },
    });
    doc.save(`plasper_finans_rapor_${by}_${start}_${end}.pdf`);
    toast.success("PDF indirildi");
  };

  if (!data) return <div className="label-caps">Yükleniyor…</div>;

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Analiz & Dışa Aktarım</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Raporlar</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="export-excel" onClick={exportExcel} className="brutal-btn bg-white border border-black px-3 py-2 text-sm flex items-center gap-2">
            <FileSpreadsheet size={15} /> Excel
          </button>
          <button data-testid="export-pdf" onClick={exportPDF} className="brutal-btn bg-black text-white px-3 py-2 text-sm flex items-center gap-2">
            <FileDown size={15} /> PDF
          </button>
        </div>
      </header>

      <div className="brutal-card p-4 flex flex-wrap gap-3 items-end">
        <div className="text-sm">
          <div className="label-caps mb-1">Rapor Bazı</div>
          <div className="flex border border-black">
            {[["date", "İşlem Tarihi"], ["due_date", "Vade Tarihi"]].map(([k, l]) => (
              <button
                key={k}
                data-testid={`report-by-${k}`}
                type="button"
                onClick={() => setBy(k)}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest ${by === k ? "bg-black text-white" : "bg-white"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <label className="text-sm">
          <div className="label-caps mb-1">Başlangıç</div>
          <input type="date" data-testid="report-start" value={start} onChange={(e) => setStart(e.target.value)} className="border border-black/20 px-3 py-1.5" />
        </label>
        <label className="text-sm">
          <div className="label-caps mb-1">Bitiş</div>
          <input type="date" data-testid="report-end" value={end} onChange={(e) => setEnd(e.target.value)} className="border border-black/20 px-3 py-1.5" />
        </label>
        <button data-testid="report-apply" onClick={apply} className="brutal-btn bg-black text-white px-4 py-1.5 text-sm">Uygula</button>
        <div className="text-[11px] text-neutral-500 ml-auto max-w-md">
          <b>Vade Tarihi</b> seçildiğinde yalnızca vade tarihi girilmiş kayıtlar dahil edilir; grafikler ve toplamlar vade bazlı hesaplanır.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label={`Gelir (${by === "due_date" ? "Vade" : "İşlem"})`} value={data.total_income} tone="pos" />
        <Stat label={`Gider (${by === "due_date" ? "Vade" : "İşlem"})`} value={data.total_expense} tone="neg" />
        <Stat label="Net" value={data.net} tone={data.net >= 0 ? "pos" : "neg"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="brutal-card p-6">
          <h3 className="font-display text-lg font-bold">
            Günlük Nakit Akışı <span className="text-xs text-neutral-500 font-normal ml-1">({by === "due_date" ? "Vade" : "İşlem"} bazlı)</span>
          </h3>
          <div className="mt-4 h-[280px]">
            <ResponsiveContainer>
              <LineChart data={data.daily}>
                <CartesianGrid strokeDasharray="2 3" stroke="#e5e5e5" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
                <Tooltip formatter={(v) => fmtTRY(v)} />
                <Legend />
                <Line dataKey="income" name="Gelir" stroke="#059669" strokeWidth={1.8} dot={false} />
                <Line dataKey="expense" name="Gider" stroke="#E11D48" strokeWidth={1.8} dot={false} />
                <Line dataKey="net" name="Net" stroke="#0A0A0A" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="brutal-card p-6">
          <h3 className="font-display text-lg font-bold">Kategori Bazlı Gider</h3>
          <div className="mt-4 h-[280px]">
            {data.expense_by_category.length === 0 ? <div className="h-full flex items-center justify-center text-sm text-neutral-500">Veri yok</div> :
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.expense_by_category} dataKey="value" nameKey="name" outerRadius={100} label={({ name }) => name}>
                    {data.expense_by_category.map((c, i) => <Cell key={c.name} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtTRY(v)} />
                </PieChart>
              </ResponsiveContainer>
            }
          </div>
        </div>
      </div>

      <div className="brutal-card p-6">
        <h3 className="font-display text-lg font-bold">Kategori Bazlı Gelir</h3>
        <div className="mt-4 h-[260px]">
          <ResponsiveContainer>
            <BarChart data={data.income_by_category}>
              <CartesianGrid strokeDasharray="2 3" stroke="#e5e5e5" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
              <Tooltip formatter={(v) => fmtTRY(v)} />
              <Bar dataKey="value" fill="#0A0A0A" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="brutal-card p-6" data-testid="contact-chart-card">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="label-caps">Cari Bazlı Analiz</p>
            <h3 className="font-display text-lg font-bold">Aylık Satış / Alış Grafiği <span className="text-xs text-neutral-500 font-normal ml-1">(tüm geçmiş)</span></h3>
          </div>
          <div className="flex items-end gap-2">
            <label className="text-sm">
              <div className="label-caps mb-1">Cari</div>
              <select
                data-testid="contact-chart-select"
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
                className="border border-black/20 px-3 py-1.5 text-sm min-w-[260px] bg-white"
              >
                <option value="">— Cari seçin —</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {!contactId ? (
          <div className="mt-6 h-[320px] flex items-center justify-center text-sm text-neutral-500 border border-dashed border-black/10">
            Grafiği görmek için bir cari seçin.
          </div>
        ) : contactLoading ? (
          <div className="mt-6 h-[320px] flex items-center justify-center text-sm text-neutral-500">
            Yükleniyor…
          </div>
        ) : !contactData || contactData.monthly.length === 0 ? (
          <div className="mt-6 h-[320px] flex items-center justify-center text-sm text-neutral-500 border border-dashed border-black/10">
            Bu cari için satış/alış kaydı bulunamadı.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <MiniStat label="Toplam Satış" value={contactData.totals.sales} tone="pos" testid="ct-total-sales" />
              <MiniStat label="Toplam Alış" value={contactData.totals.purchases} tone="neg" testid="ct-total-purchases" />
              <MiniStat label="Toplam Tahsilat" value={contactData.totals.collections} tone="pos" testid="ct-total-collections" />
              <MiniStat label="Toplam Ödeme" value={contactData.totals.payments} tone="neg" testid="ct-total-payments" />
            </div>
            <div className="mt-4 h-[340px]" data-testid="contact-monthly-chart">
              <ResponsiveContainer>
                <ComposedChart data={contactData.monthly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 3" stroke="#e5e5e5" />
                  <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
                  <Tooltip
                    formatter={(v) => fmtTRY(v)}
                    itemSorter={(it) => -Math.abs(Number(it.value) || 0)}
                    filterNull
                  />
                  <Legend
                    payload={[
                      { value: "Satış", type: "square", color: "#059669" },
                      { value: "Alış", type: "square", color: "#E11D48" },
                      { value: "Tahsilat", type: "square", color: "#2563EB" },
                      { value: "Ödeme", type: "square", color: "#B45309" },
                      { value: "Net (Satış−Alış)", type: "line", color: "#0A0A0A" },
                    ]}
                  />
                  <Bar dataKey="sales" name="Satış" fill="#059669" />
                  <Bar dataKey="purchases" name="Alış" fill="#E11D48" />
                  <Bar dataKey="collections" name="Tahsilat" fill="#2563EB" />
                  <Bar dataKey="payments" name="Ödeme" fill="#B45309" />
                  <Line dataKey="net" name="Net (Satış−Alış)" stroke="#0A0A0A" strokeWidth={2} strokeDasharray="4 3" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone, testid }) {
  return (
    <div className="border border-black/10 p-3" data-testid={testid}>
      <div className="label-caps text-[10px]">{label}</div>
      <div className={`font-mono-num text-lg mt-1 ${tone === "pos" ? "text-emerald-700" : "text-rose-600"}`}>{fmtTRY(value)}</div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="brutal-card p-5">
      <div className="label-caps">{label}</div>
      <div className={`font-mono-num text-3xl mt-2 ${tone === "pos" ? "text-emerald-700" : "text-rose-600"}`}>{fmtTRY(value)}</div>
    </div>
  );
}
